import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import CommissionsPage from "./pages/CommissionsPage";
import VideoPage from "./pages/VideoPage";
import PricingPage from "./pages/PricingPage";
import AdminConsolePage from "./pages/AdminConsolePage";
import AdminGrowthPage from "./pages/AdminGrowthPage";
import TVPage from "./pages/TVPage";
import XLandingPage from "./pages/XLandingPage";
import MapPage from "./pages/MapPage";
import ProfilePage from "./pages/ProfilePage";
import FeedPage from "./pages/FeedPage";
import MyFeedPage from "./pages/MyFeedPage";
import TrailPage from "./pages/TrailPage";
import PlayerLayout from "./components/shared/PlayerLayout";
import GiftPage from "./pages/GiftPage";
import GiftClaimPage from "./pages/GiftClaimPage";
import PublicGiftRequestPage from "./pages/PublicGiftRequestPage";
import DashboardPage from "./pages/DashboardPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import SecurityPage from "./pages/SecurityPage";
import ContactPage from "./pages/ContactPage";
import InboxPage from "./pages/InboxPage";
import AdminInboxPage from "./pages/AdminInboxPage";
import MarketplacePage from "./pages/MarketplacePage";
import ExpiredMarketPage from "./pages/ExpiredMarketPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MagicLoginPage from "./pages/MagicLoginPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import XAuthCallback from "./pages/XAuthCallback";
import ClaimUsernamePage from "./pages/ClaimUsernamePage";
import InvitePage from "./pages/InvitePage";
import GroupInvitePage from "./pages/GroupInvitePage";
import QRSignInPage from "./pages/QRSignInPage";
import QRClaimPage from "./pages/QRClaimPage";
import WaitlistPage from "./pages/WaitlistPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ProtectedRoute from "./components/shared/ProtectedRoute";
import AppShell from "./components/shared/AppShell";

// App shell — pure router + global providers. Page components live in
// /pages. All persistent overlays (Header, FAB, DraftAutoPublisher,
// InstallPromptDialog, SignupGate, 2FA challenge, Capacitor deep-link
// handler) live inside <AppShell /> in /components/shared/AppShell.jsx
// so this file stays focused on routing.
function App() {
  return (
    <AuthProvider>
      <div className="App bg-black text-white min-h-screen">
        <BrowserRouter>
          <AppShell>
            <Routes>
              {/* Player surfaces share a layout route — see
                  /components/shared/PlayerLayout.jsx for rationale. */}
              <Route element={<PlayerLayout />}>
                <Route path="/" element={<FeedPage />} />
                <Route path="/feed" element={<MyFeedPage />} />
                <Route path="/:username" element={<ProfilePage />} />
                {/* /map is part of the player layout family — same persistent
                    shell (Header + FAB + PlayerProvider). The Leaflet instance
                    itself lives in lib/mapInstance.js as a module-level
                    singleton so revisiting /map after navigating away
                    re-uses the same L.map (no tile reload flicker). */}
                <Route path="/map" element={<MapPage />} />
              </Route>
              <Route path="/trail" element={<ProtectedRoute><TrailPage /></ProtectedRoute>} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/video/:id" element={<VideoPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminConsolePage /></ProtectedRoute>} />
              <Route path="/admin/growth" element={<ProtectedRoute><AdminGrowthPage /></ProtectedRoute>} />
              <Route path="/tv" element={<TVPage />} />
              <Route path="/x/welcome" element={<XLandingPage />} />
              <Route path="/admin/inbox" element={<ProtectedRoute><AdminInboxPage /></ProtectedRoute>} />
              <Route path="/admin/inbox/:threadId" element={<ProtectedRoute><AdminInboxPage /></ProtectedRoute>} />
              <Route path="/commissions" element={<CommissionsPage />} />
              <Route path="/gifts" element={<ProtectedRoute><GiftPage /></ProtectedRoute>} />
              <Route path="/gift/claim/:token" element={<GiftClaimPage />} />
              <Route path="/gift-for/:slug" element={<PublicGiftRequestPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
              <Route path="/inbox/:threadId" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/marketplace/expired" element={<ExpiredMarketPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/magic-login/:token" element={<MagicLoginPage />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/auth/x/callback" element={<XAuthCallback />} />
              <Route path="/claim-username" element={<ClaimUsernamePage />} />
              <Route path="/invite/g/:token" element={<GroupInvitePage />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/qr-signin" element={<QRSignInPage />} />
              <Route path="/qr-claim/:handoffId" element={<QRClaimPage />} />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
            </Routes>
          </AppShell>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </div>
    </AuthProvider>
  );
}

export default App;
